import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { PlayerPopupProvider } from './contexts/PlayerPopupContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ActiveMatchProvider, useActiveMatch } from './contexts/ActiveMatchContext';
import PlayerPopup from './components/PlayerPopup';
import Navbar from './components/Navbar';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AuthCallback from './pages/AuthCallback';
import SchoolSelect from './pages/SchoolSelect';
import ChooseUsername from './pages/ChooseUsername';
import Home from './pages/Home';
import Queue from './pages/Queue';
import Game from './pages/Game';
import Results from './pages/Results';
import Leaderboard from './pages/Leaderboard';
import About from './pages/About';
import Profile from './pages/Profile';
import PublicProfile from './pages/PublicProfile';
import Learn from './pages/Learn';
import Practice from './pages/Practice';
import Audit from './pages/Audit';
import Audit1 from './pages/Audit1';
import Admin from './pages/Admin';

// AdminRoute — requires both auth and is_admin flag.
function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="app-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#8a909e' }}>
      Loading…
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) return <Navigate to="/" replace />;
  return children;
}

// Synchronous redirect — fires on the very first render (no flash) because
// activeMatch is hydrated from localStorage before the first paint.
// Allows the game page itself and the results page to render freely.
// Allows /school-select so new users are not trapped before onboarding completes.
function GameRedirectGuard({ children }) {
  const { activeMatch } = useActiveMatch();
  const location = useLocation();

  if (activeMatch?.gameId) {
    const onGame    = location.pathname === `/game/${activeMatch.gameId}`;
    const onResults = location.pathname.startsWith('/results/');
    // Allow /queue to handle its own navigation — it drives the 3-second
    // match-found countdown and redirects via game:reconnect if the player
    // already has an active game. Redirecting here races the poll interval
    // and can bypass the countdown, causing an instant load-in.
    const onQueue   = location.pathname === '/queue';
    if (!onGame && !onResults && !onQueue) {
      return <Navigate to={`/game/${activeMatch.gameId}`} replace />;
    }
  }

  return children;
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div className="app-shell" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#8a909e' }}>
      Loading…
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Google OAuth users who haven't chosen a username yet — catch them
  // regardless of which page the OAuth redirect lands on.
  if (user.needsUsername && location.pathname !== '/choose-username') {
    return <Navigate to="/choose-username" replace />;
  }
  return children;
}

function AppInner() {
  const { user } = useAuth();

  return (
    <ActiveMatchProvider>
      <PlayerPopupProvider>
        <SocketProvider connect={!!user}>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/about" element={<About />} />
              <Route path="/audit" element={<Audit />} />
              <Route path="/audit1" element={<Audit1 />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/choose-username" element={
                <PrivateRoute><ChooseUsername /></PrivateRoute>
              } />
              <Route path="/school-select" element={
                <PrivateRoute><SchoolSelect /></PrivateRoute>
              } />
              <Route path="/*" element={
                <PrivateRoute>
                  <div className="app-shell">
                    <Navbar />
                    <PlayerPopup />
                    <GameRedirectGuard>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/queue" element={<Queue />} />
                        <Route path="/game/:gameId" element={<Game />} />
                        <Route path="/results/:gameId" element={<Results />} />
                        <Route path="/leaderboard" element={<Leaderboard />} />
                        <Route path="/learn" element={<Learn />} />
                        <Route path="/learn/practice/:skill" element={<Practice />} />
                        <Route path="/profile" element={<Profile />} />
                        <Route path="/user/:userId" element={<PublicProfile />} />
                        <Route path="/admin" element={
                          <AdminRoute><Admin /></AdminRoute>
                        } />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </GameRedirectGuard>
                  </div>
                </PrivateRoute>
              } />
            </Routes>
          </BrowserRouter>
        </SocketProvider>
      </PlayerPopupProvider>
    </ActiveMatchProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </ThemeProvider>
  );
}
